import { useEffect, useState } from "react";
import { api, hoyISO, toISODateLocal } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Search, ScrollText, ChevronDown, ChevronUp } from "lucide-react";

const hace = (d) => { const x = new Date(); x.setDate(x.getDate() - d); return toISODateLocal(x); };
const fmtVal = (v) => (v == null ? "—" : typeof v === "number" ? v.toLocaleString("es-CO") : typeof v === "object" ? JSON.stringify(v) : String(v));
const ACCION = {
  desglosar_empaque: "Desglose de empaque", editar_desglose: "Edición de desglose", corregir_cantidad_lote: "Corrección de lote", actualizar_producto: "Edición manual de ficha",
  crear_producto: "Producto creado", ajustar_stock: "Ajuste de stock", crear_pedido: "Pedido creado", confirmar_pedido: "Pedido confirmado", despachar_pedido: "Pedido despachado",
  cancelar_pedido: "Pedido cancelado", recostear_contenedor: "Recosteo de contenedor", recalcular_costo_snapshots: "Recalcular costos", registrar_pago: "Pago registrado",
};

/** Configuración → Auditoría: quién cambió qué y cuándo (productos, lotes, pedidos, pagos…). */
export default function AuditoriaPage() {
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState("");
  const [accion, setAccion] = useState("");
  const [desde, setDesde] = useState(hace(90));
  const [hasta, setHasta] = useState(hoyISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: "300" });
      if (desde) p.set("desde", desde);
      if (hasta) p.set("hasta", hasta);
      if (q.trim()) p.set("q", q.trim());
      if (tipo) p.set("entity_type", tipo);
      if (accion) p.set("action", accion);
      const { data } = await api.get(`/audit-log?${p}`);
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cargar la auditoría"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [tipo, accion, desde, hasta]);

  return (
    <div className="space-y-5" data-testid="auditoria-page">
      <header>
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Auditoría</div>
        <h1 className="font-display text-4xl font-semibold tracking-tight mt-1">¿Quién cambió qué?</h1>
        <p className="text-sm text-muted-foreground mt-1">Historial de ediciones manuales, desgloses, correcciones de lotes, pedidos y pagos. Busca por producto, código, cliente, pedido o persona.</p>
      </header>

      <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); cargar(); }} data-testid="auditoria-filtros">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Producto, código (ej. EF26-185), cliente, pedido o persona…" className="h-10 pl-9" data-testid="auditoria-buscar" />
        </div>
        <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="auditoria-tipo">
          <option value="">Todo</option>
          {(data?.entity_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={accion} onChange={(e) => setAccion(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm max-w-[220px]" data-testid="auditoria-accion">
          <option value="">Todas las acciones</option>
          {(data?.actions || []).map((a) => <option key={a} value={a}>{ACCION[a] || a}</option>)}
        </select>
        <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-10 w-auto" data-testid="auditoria-desde" />
        <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-10 w-auto" data-testid="auditoria-hasta" />
        <Button type="submit" className="h-10" disabled={loading} data-testid="auditoria-buscar-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buscar"}</Button>
        {(desde || hasta) && (
          <button type="button" onClick={() => { setDesde(""); setHasta(""); }} className="h-10 text-xs text-primary underline" data-testid="auditoria-todo-historial">Todo el historial</button>
        )}
      </form>
      {data && data.items.length === 0 && (desde || hasta) && (
        <div className="text-xs text-muted-foreground -mt-2" data-testid="auditoria-hint-fechas">Sin resultados entre {desde || "el inicio"} y {hasta || "hoy"}. Prueba <button type="button" className="text-primary underline" onClick={() => { setDesde(""); setHasta(""); }}>todo el historial</button>.</div>
      )}

      {data && (
        <div className="rounded-xl border border-border overflow-hidden" data-testid="auditoria-lista">
          {data.items.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground" data-testid="auditoria-vacio">Sin registros para estos filtros.</div>}
          {data.items.map((it, i) => <Fila key={it.id} it={it} i={i} open={open === it.id} onToggle={() => setOpen(open === it.id ? null : it.id)} />)}
          {data.items.length >= 300 && <div className="p-2 text-center text-[11px] text-muted-foreground">Mostrando los 300 más recientes · afina la búsqueda para ver más atrás.</div>}
        </div>
      )}
    </div>
  );
}

function Cambios({ before, after }) {
  const b = before && typeof before === "object" ? before : {}, a = after && typeof after === "object" ? after : {};
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])].filter((k) => JSON.stringify(b[k]) !== JSON.stringify(a[k]));
  if (!keys.length) return <span className="text-muted-foreground">{fmtVal(after ?? before)}</span>;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {keys.slice(0, 12).map((k) => (
        <span key={k} className="tabular-nums"><span className="text-muted-foreground">{k}:</span> {k in b && <><span className="line-through text-muted-foreground/70">{fmtVal(b[k])}</span> → </>}<b>{fmtVal(a[k])}</b></span>
      ))}
      {keys.length > 12 && <span className="text-muted-foreground">+{keys.length - 12} más</span>}
    </div>
  );
}

function Fila({ it, i, open, onToggle }) {
  const fecha = (it.at || "").replace("T", " ").slice(0, 16);
  return (
    <div className={`border-b border-border/60 ${i % 2 ? "bg-muted/10" : ""}`} data-testid={`auditoria-fila-${i}`}>
      <button type="button" onClick={onToggle} className="w-full text-left px-3 py-2 grid grid-cols-12 gap-2 items-start text-xs hover:bg-muted/30">
        <div className="col-span-6 sm:col-span-2 tabular-nums text-muted-foreground">{fecha}</div>
        <div className="col-span-6 sm:col-span-2 font-medium truncate">{it.actor_name || "Sistema"}</div>
        <div className="col-span-6 sm:col-span-2"><span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-semibold">{ACCION[it.action] || it.action}</span> <span className="text-muted-foreground">{it.entity_type}</span></div>
        <div className="col-span-6 sm:col-span-5 truncate">{it.entity_nombre || it.entity_id || "—"}</div>
        <div className="col-span-12 sm:col-span-1 flex justify-end"><ScrollText className="w-3.5 h-3.5 text-muted-foreground" />{open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}</div>
      </button>
      {open && (
        <div className="px-3 pb-3 text-xs space-y-1" data-testid={`auditoria-detalle-${i}`}>
          <Cambios before={it.before} after={it.after} />
          {it.metadata && Object.keys(it.metadata).length > 0 && <div className="text-muted-foreground">{fmtVal(it.metadata)}</div>}
          <div className="text-[10px] text-muted-foreground">{it.actor_email} · id {it.entity_id}</div>
        </div>
      )}
    </div>
  );
}
